import { Navigate, useLocation, type RouteObject } from 'react-router-dom';

import { AuthState } from '@/shared/authentication/utils/constants';
import { firstVisibleChildPath } from '@/shared/navigation/nav-tree';
import { useNavGateContext } from '@/shared/navigation/use-nav-gate-context';

import LoadingIndicator from '@/components/shared/LoadingIndicator/LoadingIndicator';

/**
 * Section-parent index route: land on the first nav-visible child, computed
 * from the same gated route metadata the sidebar tree derives from (spec §2 —
 * canonical landing behavior). Deep-link params (?actualPriceDate=…) survive
 * the redirect.
 */
export const SectionIndexRedirect = ({ childRoutes }: { childRoutes: RouteObject[] }) => {
  const { search } = useLocation();
  const ctx = useNavGateContext();

  // Redirecting before OIDC resolves would evaluate role gates against the wrong role.
  if (ctx.role === AuthState.initialization) return <LoadingIndicator />;

  const target = firstVisibleChildPath(childRoutes, ctx);
  if (!target) return <Navigate to="/" replace />;

  return <Navigate to={{ pathname: target, search }} replace />;
};

/** A section parent's children, prefixed with its canonical index redirect. */
export const sectionChildren = (childRoutes: RouteObject[]): RouteObject[] => [
  { index: true, element: <SectionIndexRedirect childRoutes={childRoutes} /> },
  ...childRoutes
];
